import type { WretchError } from 'wretch'
import wretch from 'wretch'
import QueryStringAddon from 'wretch/addons/queryString'
import { getActiveWorkspaceId } from './active-workspace.js'
import { getAccessTokenFromProvider } from './auth-token.js'
import { getE2EUserHeader } from './e2e.js'
import { getImpersonatedUserId } from './impersonation.js'
import { getNotifier } from './notifier.js'

interface ApiConfig {
  baseUrl: string
  e2e: boolean
}

let cfg: ApiConfig | null = null

export function configureApi(opts: { baseUrl: string; e2e?: boolean }): void {
  cfg = { baseUrl: opts.baseUrl, e2e: opts.e2e ?? false }
  cachedApi = null
}

function requireConfig(): ApiConfig {
  if (!cfg) throw new Error('configureApi() must be called before any request')
  return cfg
}

const authMiddleware = (next: any) => async (url: string, opts: any) => {
  const c = requireConfig()
  if (!c.e2e) {
    const token = await getAccessTokenFromProvider()
    if (token) {
      opts.headers = {
        ...opts.headers,
        Authorization: `Bearer ${token}`,
      }
    }
  }

  if (c.e2e) {
    const userHeader = getE2EUserHeader()
    if (userHeader) {
      opts.headers = {
        ...opts.headers,
        'x-doneverse-test-user': userHeader,
      }
    }
  }
  return next(url, opts)
}

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

// Paths that bypass the impersonation read-only guard. These are actions taken
// BY the admin (not on behalf of the impersonated user), so they must always
// be allowed and must NOT have `user_id` appended — the admin's own identity
// is what should reach the API.
const IMPERSONATION_BYPASS_PATHS = ['/v1/product-feedback']

function isImpersonationBypass(url: string): boolean {
  return IMPERSONATION_BYPASS_PATHS.some((p) => {
    const i = url.indexOf(p)
    if (i === -1) return false
    const next = url[i + p.length]
    return next === undefined || next === '?' || next === '/'
  })
}

const STATUS_MESSAGES: Record<number, string> = {
  400: 'Bad request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not found',
  409: 'Conflict',
  500: 'Internal server error',
}

function fallbackStatusMessage(response: Response) {
  const statusText = response.statusText.trim()
  if (statusText) return statusText

  const statusMessage = STATUS_MESSAGES[response.status]
  return statusMessage ? `${response.status} ${statusMessage}` : `HTTP ${response.status}`
}

/** The JSON body of a failed response, or null when it does not have one. */
async function parseApiErrorBody(response: Response): Promise<Record<string, unknown> | null> {
  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.toLowerCase().includes('application/json')) return null

  const body = await response
    .clone()
    .json()
    .catch(() => null)

  return body && typeof body === 'object' ? (body as Record<string, unknown>) : null
}

function messageFromErrorBody(body: Record<string, unknown> | null) {
  const message = body?.error ?? body?.message
  return typeof message === 'string' && message.trim() ? message : null
}

async function normalizeApiError(error: WretchError, response: Response) {
  const body = await parseApiErrorBody(response)

  /**
   * Attach the parsed body to the error.
   *
   * Wretch does NOT do this: its resolver sets `response`, `status` and a
   * `message` holding the raw body text, and leaves `json`/`text` unset
   * (wretch/dist/resolver.js). So an error body carrying more than a message
   * was unreachable — and some do: the CMS publish guards answer a 422 with
   * the list of what is still missing, which is the whole point of the
   * response. Callers read it through `readErrorBody`.
   *
   * Named `json` because that is the property wretch's own docs describe, so a
   * caller reaching for it finds what it expects.
   */
  if (body) (error as WretchError & { json?: unknown }).json = body

  const apiMessage = messageFromErrorBody(body)

  if (apiMessage) {
    error.message = apiMessage
  } else if (!error.message.trim()) {
    error.message = fallbackStatusMessage(response)
  }

  const errorWithCause = error as WretchError & { cause?: unknown }
  if (errorWithCause.cause instanceof Error && !errorWithCause.cause.message.trim()) {
    delete errorWithCause.cause
  }

  return error
}

const workspaceHeaderMiddleware = (next: any) => async (url: string, opts: any) => {
  const workspaceId = getActiveWorkspaceId()
  if (workspaceId) {
    opts.headers = {
      ...opts.headers,
      'x-doneverse-workspace-id': workspaceId,
    }
  }
  return next(url, opts)
}

const impersonationMiddleware = (next: any) => async (url: string, opts: any) => {
  const userId = getImpersonatedUserId()
  if (userId) {
    if (isImpersonationBypass(url)) {
      return next(url, opts)
    }
    const method = (opts.method || 'GET').toUpperCase()
    if (WRITE_METHODS.has(method)) {
      getNotifier().error('Read-only while impersonating')
      return new Response(null, { status: 403, statusText: 'Impersonation is read-only' })
    }
    const hashIndex = url.indexOf('#')
    const hash = hashIndex === -1 ? '' : url.slice(hashIndex)
    const urlWithoutHash = hashIndex === -1 ? url : url.slice(0, hashIndex)
    const queryIndex = urlWithoutHash.indexOf('?')
    const path = queryIndex === -1 ? urlWithoutHash : urlWithoutHash.slice(0, queryIndex)
    const search = queryIndex === -1 ? '' : urlWithoutHash.slice(queryIndex + 1)
    const params = new URLSearchParams(search)
    // Some admin services already address a user explicitly. Replacing the
    // value keeps the cross-cutting impersonation middleware idempotent and
    // prevents Express from parsing duplicate user_id values as an array.
    params.set('user_id', userId)
    url = `${path}?${params.toString()}${hash}`
  }
  return next(url, opts)
}

function buildApi() {
  const c = requireConfig()
  return wretch(c.baseUrl)
    .addon(QueryStringAddon)
    .customError(normalizeApiError)
    .middlewares([authMiddleware, workspaceHeaderMiddleware, impersonationMiddleware])
}

type Api = ReturnType<typeof buildApi>

let cachedApi: Api | null = null

export function getApi(): Api {
  if (!cachedApi) cachedApi = buildApi()
  return cachedApi
}

/**
 * Ergonomic accessor: `api.get(...)`, `api.post(...)`, etc. Lazily resolves
 * to the underlying wretch instance built from `configureApi()`. Methods are
 * bound so chained calls (`.json()`, `.res()`) keep working.
 */
export const api: Api = new Proxy({} as Api, {
  get(_target, prop) {
    const inst = getApi() as unknown as Record<string | symbol, unknown>
    const value = inst[prop as string]
    return typeof value === 'function' ? (value as Function).bind(inst) : value
  },
}) as Api
